import { useState, useCallback } from 'react';
import {
  Consignment,
  ConsignmentResponse,
  SearchConsignmentsRequest,
  ACLAuthorizationRequest,
  SearchConsignmentsPostContentTypeEnum,
  CreateConsignmentContentTypeEnum,
} from '@/api/bol';
import { useSnackbarNotification } from '@/hooks/useSnackbarNotification';
import { getConsignmentsApi } from '@/services/bolApiClient';
import { extractErrorMessage, formatOperationError } from '@/utils/errorUtils';
import { getLogisticsObjectDetailPath } from '@/types/logisticsObject';
import { getEffectiveLogisticsObjectSearchQuery } from '@/utils/searchDefaults';

export interface UseConsignmentsReturn {
  consignments: ConsignmentResponse[];
  loading: boolean;
  searchError: string | null;
  selectedConsignment: ConsignmentResponse | null;
  setSelectedConsignment: (consignment: ConsignmentResponse | null) => void;
  fetchConsignments: (page?: number, pageSize?: number, searchBody?: SearchConsignmentsRequest) => Promise<boolean>;
  createConsignment: (consignmentData: unknown, memberIdentifier: string, aclAuthorizations?: ACLAuthorizationRequest[]) => Promise<void>;
  deleteConsignment: (consignmentId: string) => Promise<void>;
  setTotal: (total: number) => void;
  total: number;
}

export const useConsignments = (): UseConsignmentsReturn => {
  const [items, setItems] = useState<ConsignmentResponse[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);
  const [selectedConsignment, setSelectedConsignment] = useState<ConsignmentResponse | null>(null);
  const [total, setTotal] = useState(0);

  const { showSuccess, showError } = useSnackbarNotification();

  const fetchConsignments = useCallback(async (page: number = 0, pageSize: number = 10, searchBody?: SearchConsignmentsRequest): Promise<boolean> => {
    const normalizedSearchBody = getEffectiveLogisticsObjectSearchQuery(searchBody);
    setLoading(true);
    setSearchError(null);

    try {
      const api = getConsignmentsApi();
      const response = await api.searchConsignmentsPost({
        contentType: SearchConsignmentsPostContentTypeEnum.ApplicationJson,
        searchConsignmentsRequest: normalizedSearchBody,
        limit: pageSize,
        offset: page * pageSize,
      });

      setItems(response.results || []);
      setTotal(response.pagination?.total || 0);
      return true;
    } catch (err: unknown) {
      const errorMessage = await extractErrorMessage(err, 'Failed to fetch consignments');
      setSearchError(errorMessage);
      showError(errorMessage);
      return false;
    } finally {
      setLoading(false);
    }
  }, [showError]);

  const createConsignment = useCallback(async (consignmentData: unknown, memberIdentifier: string, aclAuthorizations?: ACLAuthorizationRequest[]) => {
    try {
      const api = getConsignmentsApi();

      const createdConsignment = await api.createConsignment({
        contentType: CreateConsignmentContentTypeEnum.ApplicationJson,
        xBOLMemberIdentifier: memberIdentifier,
        consignment: consignmentData as Consignment,
        source: true,
        xBOLACLAuthorizations: aclAuthorizations ? JSON.stringify(aclAuthorizations) : undefined,
      });

      const createdConsignmentId = createdConsignment?.id;

      showSuccess(
        'Consignment created successfully',
        createdConsignmentId
          ? {
              link: {
                label: 'View details',
                href: getLogisticsObjectDetailPath('consignment', createdConsignmentId),
              },
            }
          : undefined,
      );
    } catch (err: unknown) {
      const errorMessage = await formatOperationError(err, 'Create consignment', 'Failed to create consignment');
      showError(errorMessage);
      throw err;
    }
  }, [showSuccess, showError]);

  const deleteConsignment = useCallback(async (consignmentId: string) => {
    try {
      const api = getConsignmentsApi();
      await api.deleteConsignmentById({ consignmentId });
      showSuccess('Consignment deleted successfully');
    } catch (err: unknown) {
      const errorMessage = await formatOperationError(err, 'Delete consignment', 'Failed to delete consignment');
      showError(errorMessage);
      throw err;
    }
  }, [showSuccess, showError]);

  return {
    consignments: items,
    loading,
    searchError,
    selectedConsignment,
    setSelectedConsignment,
    fetchConsignments,
    createConsignment,
    deleteConsignment,
    setTotal,
    total,
  };
};
